import { homeOfficeAppendMax, homeOfficeAppendMin, idAppend } from "../../meta-admin.functions";
import {
  CheckInSettings,
  AdvancedBookingSettings,
  MetaData,
  IndependentBookingSetting
} from "../../../Login/typings/login.types";
import { FileObject } from "../../../FileDropzone/typings/FileObject.type";

/** Default metadata for create-company flow; used to drive the same accordion UI as company card settings. */
export function getDefaultMetaDataForCreate(brandColor: string): MetaData {
  return {
    currency: "EUR",
    defaultTimezone: "Europe/Berlin",
    maxAdvancedZoneBookingLength: 10,
    minMobileWorkingDays: 0,
    maxMobileWorkingDays: 5,
    checkinSettings: [],
    maxAllowedNoBookingTime: 20,
    bookingNormHours: 8,
    hidePastBookings: false,
    showBookingStatistics: true,
    allowBookingCancellations: false,
    teamBookingAllowed: true,
    workplaceBookingEnabled: true,
    bookingsOnSaturdaysAllowed: false,
    bookingsOnSundaysAllowed: false,
    calendarSyncPossible: false,
    mobileWorkingEnabled: false,
    homeOfficePolicyObligatory: false,
    bookingAssistenceActive: false,
    activateOfficeSafety: false,
    activateBookingServices: false,
    color: brandColor
  };
}

/** raw company to be converted into a form data object */
interface RawCompanyData {
  /** true, if the creating user is administrator */
  isTenantAdmin: boolean;
  /** The theme color of the company, to be displayed as primary color in mui components */
  brandColor: string;

  /** if enabled, hide past booking schedule in the calendar */
  hidePastBookings: boolean;
  /** if enabled, show booking statistics(percentage) for booking time of instant booking location at Home page */
  showBookingStatistics: boolean;
  /** if enabled, activate office safety (i.e. showing the first aid users booking) */
  activateOfficeSafety: boolean;
  /** if enabled, allow cancellation by team when team booking is also allowed */
  allowBookingCancellations: boolean;
  /** if enabled, shows schedule data between colleagues */
  showPastScheduleDataOfColleagues?: boolean;
  /** if enabled, allows to make team booking */
  teamBookingAllowed: boolean;
  /** if enabled, allows workplace booking as a module */
  workplaceBookingEnabled: boolean;
  /** if enabled, allows to make bookings on saturdays */
  bookingsOnSaturdaysAllowed: boolean;
  /** if enabled, allows to make bookings on sundays */
  bookingsOnSundaysAllowed: boolean;
  /** if enabled, allows to calendar sync integration  */
  calendarSyncPossible: boolean;
  /** if enabled, allows booking days as home office */
  homeOffice: {
    homeOfficeDays: [number, number];
    enabled: boolean;
    mandatory: boolean;
  };
  /** logo image for the company */
  logoFile?: FileObject[];
  /** the count of hours selected by default when checking in, should be 8 */
  bookingNormHours: number;
  /** */
  automatedWorkplaceBookingReflectionTime: number;
  /** */
  noBookingTime: number;
  /** company booking meta id */
  id: number;
  /** only set to update a company after creating one */
  createdCompanyId?: number;
  bookingAssistenceActive: boolean;
  /** check-in settings */
  checkinSettings?: Array<CheckInSettings>;
  /** maximum advanced booking time settings  */
  advancedBookingSettings: Array<AdvancedBookingSettings>;
  /** activate booking service order */
  activateBookingServices: boolean;
  /** independent booking settings per booking type */
  independentBookingSettings?: IndependentBookingSetting[];
}

/**
 * takes mutation results and converts them to form data
 * @param dataAxios
 * @param isTenantAdmin
 * @param brandColor
 * @param hidePastBookings
 * @param showBookingStatistics
 * @param activateOfficeSafety
 * @param allowBookingCancellations
 * @param teamBookingAllowed
 * @param bookingsOnSaturdaysAllowed
 * @param bookingsOnSundaysAllowed
 * @param homeOffice
 * @param logoFile
 * @param bookingNormHours
 * @param automatedWorkplaceBookingReflectionTime
 * @param noBookingTime
 * @param id
 * @param checkinSettings
 * @param advancedBookingSettings
 * @param activateBookingServices
 */
export function createFormMetadata({
  brandColor,
  hidePastBookings,
  showBookingStatistics,
  activateOfficeSafety,
  allowBookingCancellations,
  teamBookingAllowed,
  workplaceBookingEnabled,
  bookingsOnSaturdaysAllowed,
  bookingsOnSundaysAllowed,
  calendarSyncPossible,
  homeOffice,
  logoFile,
  bookingNormHours,
  automatedWorkplaceBookingReflectionTime,
  noBookingTime,
  id,
  createdCompanyId,
  bookingAssistenceActive,
  checkinSettings,
  advancedBookingSettings,
  activateBookingServices,
  independentBookingSettings
}: RawCompanyData): FormData {
  const data = new FormData();

  data.append("companyId", `${createdCompanyId}`);

  data.append("hidePastBookings", String(hidePastBookings));
  data.append("showBookingStatistics", String(showBookingStatistics));
  data.append("activateOfficeSafety", String(activateOfficeSafety));
  data.append("activateBookingServices", String(activateBookingServices));
  data.append("allowBookingCancellations", String(allowBookingCancellations));
  data.append("homeOfficePolicyObligatory", String(homeOffice.mandatory));
  data.append("mobileWorkingEnabled", String(homeOffice.enabled));
  data.append("bookingsAreAnonymous", "true");
  data.append("color", brandColor);

  logoFile && data.append("file", logoFile[0].file);

  data.append("bookingNormHours", String(bookingNormHours));

  data.append(
    "automatedWorkplaceBookingReflectionTime",
    String(automatedWorkplaceBookingReflectionTime)
  );
  data.append("teamBookingAllowed", String(teamBookingAllowed));
  data.append("workplaceBookingEnabled", String(workplaceBookingEnabled));
  data.append("bookingsOnSaturdaysAllowed", String(bookingsOnSaturdaysAllowed));
  data.append("bookingsOnSundaysAllowed", String(bookingsOnSundaysAllowed));
  data.append("calendarSyncPossible", String(calendarSyncPossible));

  homeOfficeAppendMin(homeOffice, data);
  homeOfficeAppendMax(homeOffice, data);

  data.append("id", idAppend(id));
  data.append("maxAllowedNoBookingTime", String(noBookingTime));
  data.append("bookingAssistenceActive", String(bookingAssistenceActive));
  data.append("checkinSettings", JSON.stringify(checkinSettings));
  data.append("advancedBookingSettings", JSON.stringify(advancedBookingSettings));
  if (independentBookingSettings) {
    data.append("independentBookingSettings", JSON.stringify(independentBookingSettings));
  }

  return data;
}
